import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  ToggleButtonGroup, 
  ToggleButton, 
  Button, 
  Alert,
  FormControlLabel,
  Checkbox,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import CompanyAutocomplete from './CompanyAutocomplete';
import { submitFeedback, createCompany, testConnection } from '../services/api';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { addCompanyToList } from '../data/companies';

const PROBLEM_EXAMPLES = [
  "way to automatically summarize long emails",
  "app that finds parking spots in real time",
  "food delivery service for home-cooked meals",
  "way to try on clothes virtually",
  "service that teaches kids to code through games"
];

const AMOUNT_EXAMPLES = [
  "5",
  "10",
  "20",
  "50",
  "100"
];

const MAX_PROBLEM_LENGTH = 280; // Twitter's character limit
const COUNTER_THRESHOLD = MAX_PROBLEM_LENGTH * 0.5; // Show counter after 50%

// Add these new animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

const glowVariants = {
  initial: { opacity: 0.5 },
  animate: {
    opacity: [0.5, 0.7, 0.5],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const LandingPage = () => {
  const [problem, setProblem] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [type, setType] = useState('existing');
  const [serverConnected, setServerConnected] = useState(false);
  const companyAutocompleteRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentExampleIndex((prev) => (prev + 1) % PROBLEM_EXAMPLES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await testConnection();
      setServerConnected(isConnected);
      console.log('Server connection status:', isConnected);
    };
    checkConnection();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleCompanySelect = (company) => {
    if (!company) {
      setSelectedCompanies([]);
      return;
    }

    if (company.type === 'startup') {
      setSelectedCompanies([]);
      setType('startup');
    } else {
      setSelectedCompanies([company]);
      setType('existing');
    }
  };

  const handleRemoveCompany = (companyId) => {
    if (!companyId) return;
    setSelectedCompanies(selectedCompanies.filter(c => c?.id !== companyId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted) {
      setError('Please accept the privacy policy and terms');
      return;
    }
    
    try {
      const isNewCompany = selectedCompanies[0]?.isNew;
      let companyData = type === 'startup' 
        ? { type: 'startup' }
        : {
            name: selectedCompanies[0]?.name,
            type: 'existing',
            isNew: isNewCompany
          };

      const submissionData = {
        problem: problem.trim(),
        company: companyData,
        amount: parseFloat(amount),
        frequency,
        email: email?.trim()
      };

      const response = await submitFeedback(submissionData);
      
      if (response.ok) {
        // If we successfully added a new company, add it to the local suggestions
        if (isNewCompany) {
          addCompanyToList({
            name: selectedCompanies[0].name,
            type: 'existing'
          });
          
          // Force CompanyAutocomplete to refresh its list
          if (companyAutocompleteRef.current) {
            companyAutocompleteRef.current.refreshCompanies();
          }
        }

        // Reset form
        setProblem('');
        setSelectedCompanies([]);
        setAmount('');
        setEmail('');
        setShowSuccess(true);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Failed to submit feedback');
    }
  };

  const renderCharacterCount = () => {
    if (problem.length >= COUNTER_THRESHOLD) {
      return (
        <Typography
          variant="caption"
          sx={{
            color: problem.length >= MAX_PROBLEM_LENGTH ? 'error.main' : 'rgba(255,255,255,0.6)',
            textAlign: 'right',
            width: '100%',
            mt: 1
          }}
          data-testid="char-counter"
        >
          {problem.length}/{MAX_PROBLEM_LENGTH} characters
        </Typography>
      );
    }
    return null;
  };

  return (
    <Box
      component={motion.form}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      onSubmit={handleSubmit}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1a1a 100%)',
        color: '#fff',
        position: 'relative',
        py: 8,
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box
        component={motion.div}
        variants={glowVariants}
        initial="initial"
        animate="animate"
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '40vw',
          height: '40vw',
          background: 'radial-gradient(circle, rgba(62,174,255,0.15) 0%, rgba(62,174,255,0) 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 1,
        }}
      />
      <Box
        component={motion.div}
        variants={glowVariants}
        initial="initial"
        animate="animate"
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '30vw',
          height: '30vw',
          background: 'radial-gradient(circle, rgba(255,99,71,0.15) 0%, rgba(255,99,71,0) 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 1,
        }}
      />

      {/* Content container */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '1200px',
          width: '90%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          backgroundColor: 'rgba(20, 20, 20, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          p: { xs: 4, md: 6 },
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header Section */}
        <motion.div variants={itemVariants}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { 
                xs: '3.5rem',  // Increased from 3rem
                sm: '4rem',    // Added medium breakpoint
                md: '5.5rem'   // Increased from 4.5rem
              },
              fontWeight: 800,
              textAlign: 'center',
              background: 'linear-gradient(45deg, #3EAEFF 0%, #FF6347 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              letterSpacing: '-0.02em',
              textShadow: '0 0 40px rgba(62,174,255,0.3)',
            }}
          >
            Complain, Whine, Demand...
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            mb: 4,
            gap: 4
          }}>
            {/* Left Box - Consumers */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'  // Center the title container
              }}>
                <Box component="span" sx={{ 
                  color: '#3EAEFF', 
                  fontWeight: 600,
                  textShadow: '0 0 20px rgba(62,174,255,0.3)',
                  display: 'block',
                  mb: 2,
                  textAlign: 'center'  // Center the title text
                }}>
                  CONSUMERS:
                </Box>
                <Box component="span" sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  textAlign: 'center',
                  width: '100%'  // Ensure full width for left alignment
                }}>
                  <span>Ask for features from an Existing Business!</span>
                  <span>Demand the creation of a BRAND NEW STARTUP!</span>
                </Box>
              </Box>
            </Box>

            {/* Right Box - Entrepreneurs */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'  // Center the title container
              }}>
                <Box component="span" sx={{ 
                  color: '#FF6347', 
                  fontWeight: 600,
                  textShadow: '0 0 20px rgba(255,99,71,0.3)',
                  display: 'block',
                  mb: 2,
                  textAlign: 'center'  // Center the title text
                }}>
                  ENTREPRENEURS:
                </Box>
                <Box component="span" sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  textAlign: 'center',
                  width: '100%'  // Ensure full width for left alignment
                }}>
                  <span>Get Startup ideas that people actually want!</span>
                  <span>Learn what your customers are unhappy about!</span>
                  <span>Send them a reward for their ideas!</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Form Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.2rem', md: '3rem' },  // Increased from 1.8rem/2.5rem
              fontWeight: 800,
              textAlign: 'center',
              background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4,
              mt: 6,  // Added margin top for separation
            }}
          >
            Why isn't there a fucking...
          </Typography>

          {/* Rest of the form fields with enhanced styling */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Problem input with animation */}
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <TextField
                value={problem}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_PROBLEM_LENGTH) {
                    setProblem(e.target.value);
                  }
                }}
                variant="standard"
                placeholder={PROBLEM_EXAMPLES[currentExampleIndex]}
                fullWidth
                inputProps={{
                  maxLength: MAX_PROBLEM_LENGTH,
                  'data-testid': 'problem-input'
                }}
                sx={{ 
                  '& .MuiInput-root': {
                    color: 'white',
                    fontSize: '2rem',
                    '&:before': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:after': { borderColor: '#3EAEFF' },
                    '&:hover:before': { borderColor: 'rgba(255,255,255,0.4)' },
                  },
                  '& .MuiInput-input': {
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:focus': {
                      transform: 'scale(1.02)',
                    }
                  }
                }}
              />
              {renderCharacterCount()}
            </motion.div>

            {/* Company section */}
            <motion.div variants={itemVariants}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                '& .MuiAutocomplete-root': {
                  minHeight: '56px',
                  width: '100%'
                },
                '& .MuiInputBase-root': {
                  minHeight: '56px',
                  fontSize: { xs: '1rem', md: '1rem' },
                  padding: { xs: '8px', md: '13px' }
                },
                '& .MuiSelect-select': {
                  minHeight: '56px',
                  padding: { xs: '12px 16px', md: '8px 16px' }
                },
                '& .MuiToggleButton-root': {
                  minHeight: '56px',
                  padding: { xs: '12px 16px', md: '8px 16px' },
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }
              }}>
                <Typography sx={{ 
                  fontSize: '2rem',
                  opacity: 0.8,
                  lineHeight: '2rem',
                  mt: 0.5
                }}>
                  at
                </Typography>
                <CompanyAutocomplete 
                  ref={companyAutocompleteRef}
                  onCompanySelect={handleCompanySelect}
                  darkMode={true}
                  isLandingPage={true}
                />
                {type === 'existing' && selectedCompanies.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCompanies.map(company => (
                      <Chip
                        key={company.id}
                        label={company.name}
                        onDelete={() => handleRemoveCompany(company.id)}
                        sx={{
                          bgcolor: 'rgba(62,174,255,0.2)',
                          color: 'white',
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(255,255,255,0.7)',
                            '&:hover': { color: 'white' }
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </motion.div>

            {/* Payment section */}
            <motion.div variants={itemVariants}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },  // Stack vertically on mobile
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 3,
                mb: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' }
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: { xs: '100%', md: 'auto' }
                }}>
                  <Typography sx={{ fontSize: '1.5rem', opacity: 0.8 }}>I'd pay</Typography>
                  <TextField
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    variant="standard"
                    placeholder={AMOUNT_EXAMPLES[currentExampleIndex]}
                    type="number"
                    step="0.01"
                    sx={{ 
                      width: { xs: '100%', md: '120px' },
                      '& .MuiInput-root': {
                        color: 'white',
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        minHeight: '56px',
                        '&:before': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&:after': { borderColor: '#3EAEFF' },
                        textAlign: 'center',
                      }
                    }}
                    InputProps={{
                      startAdornment: <Typography sx={{ color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}>$</Typography>,
                    }}
                  />
                </Box>
                <ToggleButtonGroup
                  value={frequency}
                  exclusive
                  onChange={(e, newFrequency) => newFrequency && setFrequency(newFrequency)}
                  orientation="horizontal"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    '& .MuiToggleButton-root': {
                      color: 'rgba(255,255,255,0.8)',
                      borderColor: 'rgba(255,255,255,0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      },
                      '&.Mui-selected': {
                        color: '#3EAEFF',
                        backgroundColor: 'rgba(62,174,255,0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(62,174,255,0.2)'
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="monthly">/month</ToggleButton>
                  <ToggleButton value="yearly">/year</ToggleButton>
                  <ToggleButton value="once">once</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </motion.div>
          </Box>
        </Box>

        {/* Email section with enhanced styling */}
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
        >
          <Box 
            sx={{ 
              mt: 3,
              p: 4,
              borderRadius: '16px',
              border: '1px solid rgba(62,174,255,0.2)',
              background: 'linear-gradient(135deg, rgba(62,174,255,0.1) 0%, rgba(255,99,71,0.1) 100%)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: '#3EAEFF' }}>
              Get Rewarded for Your Idea! 🎁
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 3 }}>
              We'll ask the company or startup to send you a reward or notify you if they implement your idea, but we will NEVER spam you. We Promise!
            </Typography>
            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com (optional)"
              type="email"
              required={false}
              sx={{
                width: '100%',
                '& .MuiInput-root': {
                  color: 'white',
                  '&:before': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:after': { borderColor: '#3EAEFF' },
                }
              }}
              variant="standard"
            />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255,255,255,0.6)', 
                display: 'block', 
                mt: 2,
                textAlign: 'left'
              }}
            >
              Not Required But Helps. We Never Send Spam, Just Gifts!
            </Typography>
          </Box>
        </motion.div>

        {/* Privacy Policy Dialog */}
        <Box
          sx={{
            mt: 2,
            p: 3,
            borderRadius: '16px',
            border: '1px solid rgba(62,174,255,0.2)',
            background: 'linear-gradient(135deg, rgba(62,174,255,0.05) 0%, rgba(255,99,71,0.05) 100%)',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                sx={{ 
                  color: '#3EAEFF',
                  '&.Mui-checked': {
                    color: '#3EAEFF',
                  }
                }}
              />
            }
            label={
              <Typography 
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacyDialog(true);
                }}
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  '& .link': {
                    color: '#3EAEFF',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: '#FF6347'
                    }
                  }
                }}
              >
                I Accept The <span className="link">Privacy Policy And Terms & Conditions</span>
              </Typography>
            }
          />
        </Box>

        {/* Submit Button */}
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              width: '100%',
              py: 1.5,
              background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(62,174,255,0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
              }
            }}
          >
            Submit your idea ✨
          </Button>
        </motion.div>
      </Box>

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 300,
          }}
        >
          <Alert 
            severity="error"
            onClose={() => setError('')}
            sx={{ 
              width: '100%',
              backgroundColor: 'rgba(211, 47, 47, 0.95)',
              color: 'white'
            }}
            data-testid="error-alert"
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Success Message */}
      {showSuccess && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 300,
          }}
        >
          <Alert 
            severity="success"
            onClose={() => setShowSuccess(false)}
            sx={{ 
              width: '100%',
              backgroundColor: 'rgba(46, 125, 50, 0.95)',
              color: 'white'
            }}
            data-testid="success-alert"
          >
            Thanks!
          </Alert>
        </Box>
      )}

      {/* Privacy Policy Dialog */}
      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          Terms & Privacy Policy
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom color="#3EAEFF">
            Data Usage & Sale
          </Typography>
          <Typography paragraph>
            By submitting feedback through our platform, you acknowledge and agree that your suggestions, 
            feature requests, problem descriptions, and email address will be sold to relevant companies 
            and potential startup founders. This data may be used for market research, product development, 
            or any other commercial purposes.
          </Typography>

          <Typography variant="h6" gutterBottom color="#3EAEFF" sx={{ mt: 3 }}>
            No Guarantees
          </Typography>
          <Typography paragraph>
            • There is no guarantee that any company or startup will implement your suggestions<br />
            • There is no guarantee that you will be contacted about your feedback<br />
            • There is no guarantee of any compensation or rewards<br />
            • Companies and startups are not obligated to notify you if they use your ideas
          </Typography>

          <Typography variant="h6" gutterBottom color="#3EAEFF" sx={{ mt: 3 }}>
            Data Collection & Sale
          </Typography>
          <Typography paragraph>
            We collect and may sell:<br />
            • Your feedback and suggestions<br />
            • Your email address<br />
            • Your pricing preferences and willingness to pay<br />
            • Aggregated or individual data about user demand
          </Typography>

          <Typography variant="h6" gutterBottom color="#3EAEFF" sx={{ mt: 3 }}>
            Terms of Use
          </Typography>
          <Typography paragraph>
            • You grant us perpetual rights to use, modify, and sell your submitted content<br />
            • You understand that you will not be compensated for your submissions<br />
            • You acknowledge that similar ideas may already exist or be developed independently<br />
            • You have no claim to any implementations of ideas similar to your submissions
          </Typography>

          <Typography variant="h6" gutterBottom color="#3EAEFF" sx={{ mt: 3 }}>
            Data Security
          </Typography>
          <Typography paragraph>
            While we implement reasonable security measures, we make no guarantees about the security 
            of your data once it has been sold or transferred to third parties.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          <Button 
            onClick={() => setShowPrivacyDialog(false)}
            sx={{ 
              color: 'white',
              '&:hover': { color: '#3EAEFF' }
            }}
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              setPrivacyAccepted(true);
              setShowPrivacyDialog(false);
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
              }
            }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>

      {/* Business Dashboard Link */}
      <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
        <Button
          component={Link}
          to="/dashboard"
          variant="outlined"
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.3)',
            '&:hover': {
              borderColor: '#3EAEFF',
              backgroundColor: 'rgba(62,174,255,0.1)'
            }
          }}
        >
          For Businesses
        </Button>
      </Box>

      {!serverConnected && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Cannot connect to server. Please check your connection.
        </Alert>
      )}
    </Box>
  );
};

export default LandingPage; 